/**
 * ImagenCampanaInput — Dropzone + editor de recorte 16:9 (react-easy-crop) +
 * pestaña "URL externa" (§8.3.2 ①).
 *
 * Proporción 16:9 (estándar de las gráficas promocionales del MINSA,
 * 2000×1125 px). Validaciones bloqueantes: tipo (JPG/PNG/WebP), peso máx 1 MB
 * y dimensiones mínimas 640×360. El recorte se re-comprime vía canvas a máx
 * 2000×1125 JPEG (calidad 0.85, bajando escalones si excede 1 MB). La imagen
 * queda local (blob) hasta guardar la campaña.
 */
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { ImagePlus, Info, Link2, Trash2, Upload } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  NOTA_IOS_IMAGEN,
  imagenPreviewUrl,
  type ImagenCampanaState,
} from './tiposCampana';
import styles from './ImagenCampanaInput.module.css';

const TIPOS_PERMITIDOS = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_PESO = 1 * 1024 * 1024; // 1 MB
// Estándar de las gráficas promocionales del MINSA: 16:9 (2000×1125 px).
const RATIO = 16 / 9;
const RATIO_LABEL = '16:9';
const MIN_ANCHO = 640;
const MIN_ALTO = 360;
const SALIDA_MAX_ANCHO = 2000;
const SALIDA_MAX_ALTO = 1125;
const CALIDAD_JPEG = 0.85;
// Si el JPEG a 0.85 excede 1 MB, se reintenta con estas calidades antes de
// rendirse (una 2000×1125 con mucho detalle puede pasarse del límite).
const CALIDADES_FALLBACK = [0.75, 0.65];

interface ImagenCampanaInputProps {
  imagen: ImagenCampanaState | null;
  onChange: (imagen: ImagenCampanaState | null) => void;
}

function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo leer la imagen.'));
    img.src = src;
  });
}

function canvasABlob(canvas: HTMLCanvasElement, calidad: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('No se pudo generar la imagen recortada.'));
      },
      'image/jpeg',
      calidad,
    );
  });
}

/** Recorta el área seleccionada y la re-comprime a JPEG máx 2000×1125.
 *  Empieza en calidad 0.85 y baja escalones si el resultado excede 1 MB. */
async function recortarAJpeg(src: string, area: Area): Promise<Blob> {
  const img = await cargarImagen(src);
  const escala = Math.min(1, SALIDA_MAX_ANCHO / area.width, SALIDA_MAX_ALTO / area.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(area.width * escala);
  canvas.height = Math.round(area.height * escala);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no soporta el recorte de imágenes.');
  ctx.drawImage(
    img,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  let blob = await canvasABlob(canvas, CALIDAD_JPEG);
  for (const calidad of CALIDADES_FALLBACK) {
    if (blob.size <= MAX_PESO) break;
    blob = await canvasABlob(canvas, calidad);
  }
  return blob;
}

export function ImagenCampanaInput({ imagen, onChange }: ImagenCampanaInputProps) {
  const [tab, setTab] = useState<'subir' | 'url'>('subir');
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // URL externa
  const [urlExterna, setUrlExterna] = useState(imagen?.tipo === 'url' ? imagen.url : '');
  const [errorUrl, setErrorUrl] = useState<string | null>(null);
  const [avisoUrl, setAvisoUrl] = useState<string | null>(null);
  const [verificandoUrl, setVerificandoUrl] = useState(false);

  // Editor de recorte
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixeles, setAreaPixeles] = useState<Area | null>(null);
  const [recortando, setRecortando] = useState(false);

  const previewUrl = imagenPreviewUrl(imagen);

  // Liberar el object URL del blob al desmontar o reemplazar.
  useEffect(() => {
    const url = imagen?.tipo === 'blob' ? imagen.previewUrl : null;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [imagen]);

  const validarYAbrirCrop = useCallback(async (file: File) => {
    setError(null);
    if (!TIPOS_PERMITIDOS.includes(file.type)) {
      setError('Formato no soportado. Use JPG, PNG o WebP.');
      return;
    }
    if (file.size > MAX_PESO) {
      setError('La imagen supera 1 MB. Elija una más liviana.');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    try {
      const img = await cargarImagen(objectUrl);
      if (img.naturalWidth < MIN_ANCHO || img.naturalHeight < MIN_ALTO) {
        URL.revokeObjectURL(objectUrl);
        setError(
          `La imagen mide ${img.naturalWidth}×${img.naturalHeight} px; el mínimo es ${MIN_ANCHO}×${MIN_ALTO} px.`,
        );
        return;
      }
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAreaPixeles(null);
      setCropSrc(objectUrl);
    } catch {
      URL.revokeObjectURL(objectUrl);
      setError('No se pudo leer el archivo. ¿Es una imagen válida?');
    }
  }, []);

  const cerrarCrop = useCallback(() => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [cropSrc]);

  const confirmarRecorte = async () => {
    if (!cropSrc || !areaPixeles) return;
    setRecortando(true);
    try {
      const blob = await recortarAJpeg(cropSrc, areaPixeles);
      if (blob.size > MAX_PESO) {
        setError('La imagen recortada supera 1 MB. Intente con un área menor.');
        return;
      }
      onChange({ tipo: 'blob', blob, previewUrl: URL.createObjectURL(blob) });
      setError(null);
      cerrarCrop();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo recortar la imagen.');
    } finally {
      setRecortando(false);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void validarYAbrirCrop(file);
  };

  /**
   * Valida la URL externa CARGANDO la imagen real (no por extensión: muchas
   * URLs válidas de CDN/Storage no terminan en .jpg). Lo que importa para
   * FCM y los teléfonos es que sea https, pública y devuelva una imagen.
   */
  const aplicarUrlExterna = () => {
    const url = urlExterna.trim();
    setAvisoUrl(null);
    if (!/^https:\/\//i.test(url)) {
      setErrorUrl('Debe ser una URL https.');
      return;
    }
    setErrorUrl(null);
    setVerificandoUrl(true);

    const img = new Image();
    const timeout = window.setTimeout(() => {
      img.src = '';
      setVerificandoUrl(false);
      setErrorUrl('La imagen tardó demasiado en cargar. Verifique que la URL sea pública y accesible.');
    }, 12_000);

    img.onload = () => {
      window.clearTimeout(timeout);
      setVerificandoUrl(false);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (w < MIN_ANCHO || h < MIN_ALTO) {
        setErrorUrl(`La imagen es muy pequeña (${w}×${h}). Mínimo ${MIN_ANCHO}×${MIN_ALTO} px.`);
        return;
      }
      const ratio = w / h;
      if (Math.abs(ratio - RATIO) > 0.15) {
        // Advertencia, no bloqueo — igual que el estándar de los composers
        setAvisoUrl(
          `La imagen es ${w}×${h} (proporción ${ratio.toFixed(2)}:1, no ${RATIO_LABEL}): ` +
          'los teléfonos la recortarán al centro en la notificación.',
        );
      }
      onChange({ tipo: 'url', url });
    };

    img.onerror = () => {
      window.clearTimeout(timeout);
      setVerificandoUrl(false);
      setErrorUrl('No se pudo cargar una imagen desde esa URL. Verifique que sea pública y que apunte directamente a la imagen (no a una página).');
    };

    img.src = url;
  };

  const quitarImagen = () => {
    onChange(null);
    setUrlExterna('');
    setError(null);
    setErrorUrl(null);
    setAvisoUrl(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={styles.root}>
      <div className={styles.tabs} role="tablist" aria-label="Origen de la imagen">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'subir'}
          className={`${styles.tab} ${tab === 'subir' ? styles.tabActive : ''}`}
          onClick={() => setTab('subir')}
        >
          <Upload size={13} aria-hidden="true" /> Subir imagen
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'url'}
          className={`${styles.tab} ${tab === 'url' ? styles.tabActive : ''}`}
          onClick={() => setTab('url')}
        >
          <Link2 size={13} aria-hidden="true" /> URL externa
        </button>
      </div>

      {tab === 'subir' ? (
        <>
          {previewUrl ? (
            <div className={styles.previewWrap}>
              <img src={previewUrl} alt="Imagen de la campaña" className={styles.previewImg} />
              <div className={styles.previewActions}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => inputRef.current?.click()}
                >
                  <ImagePlus size={14} aria-hidden="true" /> Cambiar
                </Button>
                <Button variant="danger" size="sm" onClick={quitarImagen}>
                  <Trash2 size={14} aria-hidden="true" /> Quitar imagen
                </Button>
              </div>
            </div>
          ) : (
            <div
              className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Subir imagen de la campaña"
            >
              <ImagePlus size={26} aria-hidden="true" className={styles.dropzoneIcon} />
              <p className={styles.dropzoneTitle}>
                Arrastre una imagen o haga clic para seleccionar
              </p>
              <p className={styles.dropzoneReqs}>
                JPG, PNG o WebP · ratio {RATIO_LABEL} · recomendado 2000×1125 · máx 1 MB
              </p>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className={styles.fileInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void validarYAbrirCrop(file);
              e.target.value = '';
            }}
            aria-hidden="true"
            tabIndex={-1}
          />
        </>
      ) : (
        <div className={styles.urlTab}>
          <Input
            label="URL de la imagen (https, pública)"
            placeholder="https://ejemplo.com/imagen"
            value={urlExterna}
            onChange={(e) => setUrlExterna(e.target.value)}
            error={errorUrl ?? undefined}
            inputMode="url"
          />
          <div className={styles.urlActions}>
            <Button
              variant="secondary"
              size="sm"
              loading={verificandoUrl}
              onClick={aplicarUrlExterna}
            >
              {verificandoUrl ? 'Verificando…' : 'Usar esta URL'}
            </Button>
            {imagen?.tipo === 'url' && (
              <Button variant="danger" size="sm" onClick={quitarImagen}>
                <Trash2 size={14} aria-hidden="true" /> Quitar imagen
              </Button>
            )}
          </div>
          {avisoUrl && (
            <p className={styles.avisoRatio} role="status">
              {avisoUrl}
            </p>
          )}
          {imagen?.tipo === 'url' && (
            <div className={styles.previewWrap}>
              <img src={imagen.url} alt="Imagen de la campaña" className={styles.previewImg} />
            </div>
          )}
        </div>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.notaIos}>
        <Info size={15} aria-hidden="true" />
        <span>{NOTA_IOS_IMAGEN}</span>
      </div>

      {/* ─── Modal de recorte 16:9 ─── */}
      <Modal open={cropSrc !== null} onClose={cerrarCrop} title="Recortar imagen" size="md">
        <div className={styles.cropBody}>
          <p className={styles.cropHint}>
            Ajuste el área visible — la imagen se recorta a proporción {RATIO_LABEL} (recomendado
            2000×1125 px).
          </p>
          <div className={styles.cropArea}>
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={RATIO}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPx) => setAreaPixeles(areaPx)}
              />
            )}
          </div>
          <label className={styles.zoomLabel}>
            Zoom
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className={styles.zoomRange}
              aria-label="Zoom del recorte"
            />
          </label>
          <div className={styles.cropActions}>
            <Button variant="secondary" onClick={cerrarCrop}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={recortando}
              disabled={!areaPixeles}
              onClick={() => void confirmarRecorte()}
            >
              Aplicar recorte
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
